<template>
  <div class="survey-screen" role="form" v-if="!isSurveyLoading">
    <ScreenHeading>
      {{ screenHeading }}
    </ScreenHeading>
    <p>{{ screenSubheading }}</p>
    <ScreenHotkeyGroup>
      <ScreenHotkeyBtn
        v-for="answer in answers"
        :key="answer.value"
        :hotkey="answer.hotkey"
        :code="answer.value"
        :text="answer.label"
        :value="answer.value"
        @select="selectAnswer"
        :active="answer.value === currentResponse?.answer"
      />
    </ScreenHotkeyGroup>
    <ScreenNextSectionBtn @navigateClick="emit('navigateNext')" :disabled="!currentResponse?.answer" />
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useAmplitude } from '@outputinc/o-analytics'
import { storeToRefs } from 'pinia'
import ScreenHeading from '@/components/shared/screens/ScreenHeading.vue'
import ScreenHotkeyBtn from '@/components/shared/screens/ScreenHotkeyBtn.vue'
import ScreenHotkeyGroup from '@/components/shared/screens/ScreenHotkeyGroup.vue'
import ScreenNextSectionBtn from '@/components/shared/screens/ScreenNextSectionBtn.vue'
import mapIndexToAlpha from '@/utils/mapIndexToAlpha'
import { useModalStore } from '@/store/ModalStore'
import { useSurveyStore } from '@/store/SurveyStore'
import { useScreenStore } from '@/store/ScreenStore'

const modalStore = useModalStore()
const { isShowModal } = storeToRefs(modalStore)

const surveyStore = useSurveyStore()
const screenStore = useScreenStore()

const { track, Events } = useAmplitude()
const { setScreenComplete } = screenStore
const { handleAction, handleSurveyResponse, setSurveyId } = surveyStore
const { currentSurvey, currentSurveyProduct, responses, loading: isSurveyLoading, surveyHasActions } = storeToRefs(surveyStore)

const props = defineProps({
  screenId: {
    type: String,
    default: ''
  },
  surveyId: {
    type: String,
    default: ''
  }
})

const surveyProperty = computed(() => props.surveyId)
const currentSurveyId = computed(() => currentSurvey.value?.id)
const currentResponse = computed(() => responses.value.find((response: any) => response.id === currentSurveyId.value))
const emit = defineEmits(['navigateNext'])
const screenHeading = computed(() => currentSurvey.value?.question)
const screenSubheading = ref('')

const answers = computed(() => {
  if (!currentSurvey.value) {
    return
  }
  return mapIndexToAlpha(currentSurvey.value?.answers, 'hotkey')
})

const selectAnswer = (option: any) => {
  const surveyResponse = {
    id: currentSurveyId.value,
    answer: option.value,
    property: surveyProperty.value
  }

  handleSurveyResponse(surveyResponse)

  if (surveyHasActions.value) {
    const modalTriggered = handleAction(option)

    if (modalTriggered) {
      return
    }
  }
  continueToNextScreen()
}

const continueToNextScreen = () => {
  setScreenComplete(true)
  setTimeout(() => emit('navigateNext'), 300)
}

watch(props, ({ surveyId }) => setSurveyId(surveyId))

// if modal is closed, proceed to next screen
watch(isShowModal, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    continueToNextScreen()
  }
})

onMounted(() => {
  track(Events.CHECKOUT_SUBSCRIPTION_QUESTION_VIEW, {
    surveyId: props.surveyId,
    hostname: window.location.host,
    products: [currentSurveyProduct.value]
  })
})
</script>
<style lang="scss" scoped>
.survey-screen {
  max-width: $grid-breakpoint-sm;
  padding: 0 $spacer-3;
  margin: $spacer-5 auto;

  @media (min-height: 769px) {
    margin: $spacer-9 0 $spacer-5;
  }

  @media screen and (min-width: $grid-breakpoint-sm) {
    padding: 0 $spacer-7;
  }
}
</style>
